const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

export type AuthUser = {
  id: number
  telegram_id: number
  username: string
  first_name: string
  last_name: string
  balance: number
}

const TOKEN_KEY = 'tg_game_crash_jwt'
/** Survives React StrictMode remount (URL is stripped on first read). */
let memoryToken: string | null | undefined

/** Read JWT from lobby handoff (?token=), sessionStorage, or memory. */
export function takeTokenFromUrl(): string | null {
  try {
    const u = new URL(window.location.href)
    const fromQuery = u.searchParams.get('token')
    if (fromQuery) {
      memoryToken = fromQuery
      try {
        sessionStorage.setItem(TOKEN_KEY, fromQuery)
      } catch {
        /* private mode */
      }
      u.searchParams.delete('token')
      const qs = u.searchParams.toString()
      window.history.replaceState(
        null,
        '',
        u.pathname + (qs ? `?${qs}` : '') + u.hash,
      )
      return fromQuery
    }
  } catch {
    /* ignore */
  }
  if (memoryToken) return memoryToken
  try {
    const stored = sessionStorage.getItem(TOKEN_KEY)
    if (stored) {
      memoryToken = stored
      return stored
    }
  } catch {
    /* ignore */
  }
  return null
}

/** Decode user_id from JWT payload (no signature verify; gateway already issued it). */
export function userIdFromToken(token: string): number {
  try {
    const part = token.split('.')[1]
    if (!part) return 0
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = atob(pad)
    const claims = JSON.parse(json) as { user_id?: number }
    const id = Number(claims.user_id)
    return Number.isFinite(id) ? id : 0
  } catch {
    return 0
  }
}

export async function loginWithInitData(
  initData: string,
): Promise<{ token: string; user: AuthUser }> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemo } = await import('../../../shared/demo/adapters/auth.ts')
    const s = loginDemo()
    return { token: s.token, user: { ...s.user, last_name: '' } }
  }
  const res = await fetch(`${API_BASE}/api/auth/telegram`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ init_data: initData }),
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? `auth failed (${res.status})`)
  }
  return res.json() as Promise<{ token: string; user: AuthUser }>
}

/** Local play without Telegram (Gateway debug mode: POST /api/auth/dev). */
export async function loginDev(
  body: { username?: string; first_name?: string; telegram_id?: number } = {},
): Promise<{ token: string; user: AuthUser }> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemo } = await import('../../../shared/demo/adapters/auth.ts')
    const s = loginDemo()
    return { token: s.token, user: { ...s.user, last_name: '' } }
  }
  const res = await fetch(`${API_BASE}/api/auth/dev`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(errBody.error ?? `dev auth failed (${res.status})`)
  }
  return res.json() as Promise<{ token: string; user: AuthUser }>
}

export type CrashRoundFairness = {
  id: string
  status: string
  algorithm: string
  client_seed: string
  server_seed_hash: string
  server_seed?: string
  crash_point?: number
}

export async function fetchCrashRound(
  token: string,
  id: string,
): Promise<CrashRoundFairness> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    // Demo history has no server proof; preserve the existing chip-only fallback.
    throw new Error('round lookup failed')
  }
  const res = await fetch(`${API_BASE}/api/games/crash/rounds/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error('round lookup failed')
  return (await res.json()) as CrashRoundFairness
}

export async function fetchBalance(token: string): Promise<number> {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { fetchBalance: demoBalance } = await import('../../../shared/demo/adapters/auth.ts')
    return demoBalance().balance
  }
  const res = await fetch(`${API_BASE}/api/wallet/balance`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error('balance failed')
  const body = (await res.json()) as { balance: number }
  return body.balance
}

export async function getInitData(): Promise<string> {
  const dev = (import.meta.env.VITE_DEV_INIT_DATA as string | undefined) ?? ''

  // Prefer legacy WebApp first — fast and reliable in TG WebView.
  try {
    const legacy = (
      window as unknown as {
        Telegram?: { WebApp?: { initData?: string; ready?: () => void } }
      }
    ).Telegram?.WebApp
    legacy?.ready?.()
    if (legacy?.initData) return legacy.initData
  } catch {
    /* ignore */
  }

  // SDK path with timeout so a hung isTMA() cannot blank the UI forever.
  try {
    const raw = await Promise.race([
      (async () => {
        const { init, isTMA, retrieveRawInitData } = await import(
          '@telegram-apps/sdk-react'
        )
        if (await isTMA('complete')) {
          init()
          return retrieveRawInitData() || ''
        }
        return ''
      })(),
      new Promise<string>((resolve) => {
        window.setTimeout(() => resolve(''), 2000)
      }),
    ])
    if (raw) return raw
  } catch {
    /* ignore */
  }
  return dev
}
